/**
 * Brand Kit → Speaker Format (PLAT-12177).
 *
 * How the showcase's speakers' Name and Title lines are built when nobody
 * typed them: name order, and the order and separator of Position and
 * Company. Stored on `branding.speakerFormat` (PLAT-12175) and read through
 * `normalizeSpeakerFormat` everywhere a speaker line is shown (PLAT-12176).
 * Untouched, it shows the default, which is exactly what every nametag showed
 * before, so opening the section changes nothing.
 */
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  deriveSpeakerName,
  deriveSpeakerTitle,
  normalizeSpeakerFormat,
  type SpeakerFormat,
} from '@src/utility/speakerRole';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import Accordion from './Accordion';

const logger = LogManager.getLogger('BrandKit:SpeakerFormat');

const SAVE_FAILED_ERROR = 'Failed to save. Please try again.';

// Generic, never a real person or firm.
const EXAMPLE = {
  firstName: 'Jane',
  lastName: 'Doe',
  role: 'CEO',
  organization: 'Acme Corp',
};

type Choice<K extends keyof SpeakerFormat> = {
  key: K;
  label: string;
  options: ReadonlyArray<{ value: SpeakerFormat[K]; label: string }>;
};

const NAME: Choice<'name'> = {
  key: 'name',
  label: 'Name',
  options: [
    { value: 'first-last', label: 'First Last' },
    { value: 'last-first', label: 'Last, First' },
  ],
};
const TITLE_ORDER: Choice<'titleOrder'> = {
  key: 'titleOrder',
  label: 'Title Order',
  options: [
    { value: 'company-position', label: 'Company, Position' },
    { value: 'position-company', label: 'Position, Company' },
  ],
};
const SEPARATOR: Choice<'titleSeparator'> = {
  key: 'titleSeparator',
  label: 'Separator',
  options: [
    { value: '|', label: '|' },
    { value: '·', label: '·' },
    { value: ',', label: ',' },
  ],
};

const sameFormat = (a: SpeakerFormat, b: SpeakerFormat) =>
  a.name === b.name &&
  a.titleOrder === b.titleOrder &&
  a.titleSeparator === b.titleSeparator;

const BrandKitSpeakerFormat = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const stored = normalizeSpeakerFormat(
    videoCreator.organization?.branding?.speakerFormat,
  );
  const [draft, setDraft] = useState<SpeakerFormat>(stored);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A showcase's branding arrives after the page mounts.
  const storedKey = JSON.stringify(stored);
  useEffect(() => {
    setDraft(normalizeSpeakerFormat(JSON.parse(storedKey)));
  }, [storedKey]);

  const dirty = !sameFormat(draft, stored);

  const choose = <K extends keyof SpeakerFormat>(
    key: K,
    value: SpeakerFormat[K],
  ) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setIsSaved(false);
    setError(null);
  };

  const handleSave = async () => {
    const organization = videoCreator.organization;
    if (!organization) return;
    setIsSaving(true);
    setError(null);
    try {
      // Into the store only once saved: a failed save keeps the choice
      // unsaved, with Save still there.
      const branding = {
        ...organization.branding!,
        speakerFormat: draft,
      };
      await datoClientStore.albumRepository?.update({
        ...organization,
        branding,
      });
      organization.branding = branding;
      setIsSaved(true);
    } catch (err) {
      logger.error('Failed to save the speaker format', extractErrorInfo(err));
      setError(SAVE_FAILED_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  const renderChoice = <K extends keyof SpeakerFormat>(choice: Choice<K>) => (
    <SRow key={choice.key}>
      <SLabel id={`speaker-format-${choice.key}`}>{choice.label}</SLabel>
      <SChips
        role="radiogroup"
        aria-labelledby={`speaker-format-${choice.key}`}
      >
        {choice.options.map(option => {
          const active = draft[choice.key] === option.value;
          return (
            <SChip
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              active={active}
              onClick={() => choose(choice.key, option.value)}
            >
              {option.label}
            </SChip>
          );
        })}
      </SChips>
    </SRow>
  );

  return (
    <Accordion
      id="speaker-format"
      title="Speaker Format"
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <SBody>
        <SHelp>
          How a speaker’s name and title read on nametags and quote cards when
          they haven’t been typed in.
        </SHelp>
        {renderChoice(NAME)}
        {renderChoice(TITLE_ORDER)}
        {renderChoice(SEPARATOR)}
        {/* Only the Name setting reaches a three-line nametag: Position and
            Company have their own lines there, in the order each template
            sets (PLAT-12179). */}
        <SRow>
          <span />
          <SNote>
            Title Order and Separator apply to two-line nametags. Three-line
            nametags show Position and Company on their own lines, in the order
            each template sets.
          </SNote>
        </SRow>
        <SRow>
          <SLabel>Example</SLabel>
          <SExamples>
            <SExampleColumn>
              <SExampleCaption>Two lines</SExampleCaption>
              <SExample data-testid="speaker-format-example">
                <SExampleName>{deriveSpeakerName(EXAMPLE, draft)}</SExampleName>
                <SExampleTitle>
                  {deriveSpeakerTitle(EXAMPLE, draft)}
                </SExampleTitle>
              </SExample>
            </SExampleColumn>
            <SExampleColumn>
              <SExampleCaption>Three lines</SExampleCaption>
              <SExample data-testid="speaker-format-example-three">
                <SExampleName>{deriveSpeakerName(EXAMPLE, draft)}</SExampleName>
                <SExampleTitle>{EXAMPLE.role}</SExampleTitle>
                <SExampleTitle>{EXAMPLE.organization}</SExampleTitle>
              </SExample>
            </SExampleColumn>
          </SExamples>
        </SRow>
        {error && <SError role="alert">{error}</SError>}
        {dirty && (
          <SActions>
            <SButton
              type="button"
              disabled={isSaving}
              onClick={() => {
                setDraft(stored);
                setError(null);
              }}
            >
              Cancel
            </SButton>
            <SButton
              type="button"
              className="save"
              disabled={isSaving}
              onClick={handleSave}
            >
              Save
            </SButton>
          </SActions>
        )}
      </SBody>
    </Accordion>
  );
});

const SBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SHelp = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

const SRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
  gap: 12px;
`;

const SLabel = styled.span`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
`;

const SChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

// The Brand Kit's chip (FontStyleConfigRow's weight chip): gold when chosen.
const SChip = styled.button<{ active: boolean }>`
  height: 28px;
  min-width: 36px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid
    ${({ active }) => (active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ active }) =>
    active ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'};
  color: ${({ active }) =>
    active ? 'var(--accent)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-normal);
  &:hover {
    border-color: ${({ active }) =>
      active ? 'var(--accent)' : 'var(--text-muted)'};
  }
  /* Focus is neutral, never coloured; a gold border already means "chosen"
     (design system, toggle buttons). Keyboard focus gets the neutral ring. */
  outline: none;
  &:focus-visible {
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const SNote = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const SExamples = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const SExampleColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SExampleCaption = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const SExample = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-left: 3px solid var(--accent);
  background: var(--surface-elevated);
  border-radius: 0 4px 4px 0;
`;

const SExampleName = styled.span`
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
`;

const SExampleTitle = styled.span`
  color: var(--text-secondary);
  font-size: var(--text-xs);
`;

const SError = styled.p`
  margin: 0;
  color: var(--destructive);
  font-size: var(--text-sm);
`;

const SActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const SButton = styled.button`
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  transition: var(--transition-normal);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text-secondary);
  &:hover:not(:disabled) {
    border-color: var(--text-muted);
    color: var(--foreground);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &.save {
    background: var(--primary);
    color: var(--primary-foreground);
    border-color: var(--primary);
  }
`;

export default BrandKitSpeakerFormat;
